import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkspace } from '../../context/WorkspaceContext';
import { apiFetch } from '../../fetchInterceptor';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Mobile-only camera FAB — sits ~80px above the bottom nav at the right edge.
 *
 * On tap, opens the OS camera (via <input type="file" capture="environment">),
 * uploads the photo to the existing OCR pipeline, and routes the user to the
 * OCR review page with the new job_id prefilled. Defaults to creating a bill
 * but can be overridden via prop.
 *
 * The hidden file input is the entire mobile camera UX — `capture="environment"`
 * tells iOS/Android to open the back camera directly, skipping the gallery
 * picker. On desktop the same input falls back to a normal file dialog
 * (which is why this FAB is gated behind `lg:hidden`).
 */
export default function MobileCameraFAB({ documentType = 'bill' }) {
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const [uploading, setUploading] = useState(false);

  const handleClick = () => {
    if (uploading) return;
    if (!currentWorkspace?.workspace_id) {
      toast.error('Pick a workspace first.');
      return;
    }
    inputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so picking the same file again still fires onChange
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      toast.error('Photo is over 50MB — please retake.');
      return;
    }

    setUploading(true);
    const toastId = toast.loading('Scanning receipt…');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', documentType);
      const url =
        `${API_URL}/api/ocr/upload?workspace_id=${encodeURIComponent(
          currentWorkspace.workspace_id,
        )}`;
      const r = await apiFetch(url, { method: 'POST', body: fd });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${r.status})`);
      }
      const data = await r.json();
      toast.success('Receipt scanned — review the extracted fields.', { id: toastId });
      navigate(
        `/invoice-ocr?job=${encodeURIComponent(data.job_id)}&type=${documentType}`,
      );
    } catch (err) {
      toast.error(err?.message || 'Receipt scan failed.', { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={uploading}
        aria-label="Scan receipt with camera"
        data-testid="mobile-camera-fab"
        className={cn(
          'lg:hidden fixed left-4 z-40 w-14 h-14 rounded-full',
          // sit above the bottom-nav (h-16 = 64px) with 16px breathing room
          'bottom-20',
          'bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white',
          'shadow-lg shadow-emerald-500/30 ring-2 ring-emerald-400/40',
          'flex items-center justify-center transition-all duration-150',
          uploading && 'opacity-70 cursor-wait',
        )}
      >
        {uploading ? (
          <Loader2 className="w-6 h-6 animate-spin" />
        ) : (
          <Camera className="w-6 h-6" />
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        // `capture="environment"` opens the rear camera directly on iOS/Android.
        capture="environment"
        onChange={handleFile}
        className="hidden"
        data-testid="mobile-camera-input"
      />
    </>
  );
}
